import { useCallback, useEffect, useId, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useUserProfileData } from '@/hooks/useUserProfileData';
import { Button } from '@/lib/base/buttons/button';
import { FormProvider, useFormContext } from '@/lib/base/forms/form-context';
import { FormField } from '@/lib/base/forms/form-field';
import { Input } from '@/lib/base/forms/input/input';
import type { SelectItemType } from '@/lib/base/forms/select/select';
import { Select } from '@/lib/base/forms/select/select';
import { LoadingIndicator } from '@/lib/base/foundations';
import {
  COMMON_TIMEZONES,
  getBrowserTimezone,
  getTimezoneOffset,
} from '@/lib/scheduling/timezone';

import { useJoinOnboardingStatus } from '../../api';
import {
  joinStepPath,
  NAME_MAX_LENGTH,
  ROLE_MAX_LENGTH,
} from '../../constants';
import type { JoinFlowState } from '../../flowState';
import { useJoinFlowState } from '../../flowState';

interface TimezoneItem {
  id: string;
  label: string;
  supportingText: string;
}

/**
 * Module-level so the render prop is a stable reference rather than a new
 * closure per render (FRONTEND_PATTERNS § stable references).
 */
function renderTimezoneItem(item: SelectItemType) {
  return (
    <Select.Item id={item.id} textValue={item.label}>
      {item.label}
      {item.supportingText && (
        <span className='text-text-tertiary ml-2 text-xs'>
          {item.supportingText}
        </span>
      )}
    </Select.Item>
  );
}

interface AboutYouStepFormProps {
  firstName: string;
  onFirstNameChange: (value: string) => void;
  lastName: string;
  onLastNameChange: (value: string) => void;
  role: string;
  onRoleChange: (value: string) => void;
  timezone: string;
  onTimezoneChange: (value: string) => void;
  onContinue: () => void;
}

function AboutYouStepForm({
  firstName,
  onFirstNameChange,
  lastName,
  onLastNameChange,
  role,
  onRoleChange,
  timezone,
  onTimezoneChange,
  onContinue,
}: AboutYouStepFormProps) {
  const { touchAll, isValid, errorSummary, scrollToField } = useFormContext();
  const firstNameErrorId = useId();
  const lastNameErrorId = useId();
  const roleErrorId = useId();
  const timezoneLabelId = useId();

  // COMMON_TIMEZONES is a CURATED list, not all ~400 IANA zones, so a resolved
  // browser zone (or a value an admin stored) can legitimately be absent from
  // it. Append it rather than dropping it: an unmatched `selectedKey` would
  // render as empty and silently discard a correct zone the moment the invitee
  // touched any other field.
  const timezoneItems = useMemo(() => {
    const items: TimezoneItem[] = COMMON_TIMEZONES.map(tz => ({
      id: tz.value,
      label: tz.label,
      supportingText: getTimezoneOffset(tz.value),
    }));
    if (timezone && !items.some(item => item.id === timezone)) {
      items.unshift({
        id: timezone,
        label: timezone.replace(/_/g, ' '),
        supportingText: getTimezoneOffset(timezone),
      });
    }
    return items;
  }, [timezone]);

  const handleTimezoneChange = useCallback(
    (key: string | number | null) => {
      if (key !== null && key !== undefined) {
        onTimezoneChange(String(key));
      }
    },
    [onTimezoneChange]
  );

  const handleContinue = useCallback(() => {
    touchAll();
    if (!isValid) {
      if (errorSummary.length > 0) {
        requestAnimationFrame(() => scrollToField(errorSummary[0].name));
      }
      return;
    }
    onContinue();
  }, [touchAll, isValid, errorSummary, scrollToField, onContinue]);

  return (
    <div data-testid='join-step-about-you' className='flex flex-col gap-6'>
      <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
        <FormField
          name='firstName'
          label='First name'
          isRequired
          errorId={firstNameErrorId}
        >
          {({ inputId, labelId, isInvalid, showError }) => (
            <Input
              id={inputId}
              aria-labelledby={labelId}
              className='w-full'
              placeholder='Ada'
              value={firstName}
              onChange={onFirstNameChange}
              name='firstName'
              maxLength={NAME_MAX_LENGTH}
              isRequired
              isInvalid={isInvalid}
              showError={showError}
              aria-describedby={isInvalid ? firstNameErrorId : undefined}
              autoFocus
            />
          )}
        </FormField>

        <FormField
          name='lastName'
          label='Last name'
          isRequired
          errorId={lastNameErrorId}
        >
          {({ inputId, labelId, isInvalid, showError }) => (
            <Input
              id={inputId}
              aria-labelledby={labelId}
              className='w-full'
              placeholder='Lovelace'
              value={lastName}
              onChange={onLastNameChange}
              name='lastName'
              maxLength={NAME_MAX_LENGTH}
              isRequired
              isInvalid={isInvalid}
              showError={showError}
              aria-describedby={isInvalid ? lastNameErrorId : undefined}
            />
          )}
        </FormField>
      </div>

      <FormField name='role' label='Your role' isRequired errorId={roleErrorId}>
        {({ inputId, labelId, isInvalid, showError }) => (
          <Input
            id={inputId}
            aria-labelledby={labelId}
            className='w-full'
            placeholder='Founder'
            value={role}
            onChange={onRoleChange}
            name='role'
            maxLength={ROLE_MAX_LENGTH}
            isRequired
            isInvalid={isInvalid}
            showError={showError}
            aria-describedby={isInvalid ? roleErrorId : undefined}
            tooltip='Helps Ahoy tailor your workspace to how you work.'
          />
        )}
      </FormField>

      <div className='flex flex-col gap-2'>
        <span
          id={timezoneLabelId}
          className='text-sm font-semibold text-[var(--color-text-primary)]'
        >
          Timezone
        </span>
        <Select.ComboBox
          placeholder='Select timezone'
          items={timezoneItems}
          selectedKey={timezone === '' ? null : timezone}
          onSelectionChange={handleTimezoneChange}
          shortcut={false}
          aria-labelledby={timezoneLabelId}
        >
          {renderTimezoneItem}
        </Select.ComboBox>
      </div>

      <Button size='lg' className='w-full' onClick={handleContinue}>
        Continue
      </Button>
    </div>
  );
}

export function AboutYouStep() {
  const { orgId } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  const { state, updateState } = useJoinFlowState();
  const status = useJoinOnboardingStatus(orgId ?? '');
  const profile = useUserProfileData();

  const handleFirstNameChange = useCallback(
    (firstName: string) => updateState({ firstName }),
    [updateState]
  );
  const handleLastNameChange = useCallback(
    (lastName: string) => updateState({ lastName }),
    [updateState]
  );
  const handleRoleChange = useCallback(
    (role: string) => updateState({ role }),
    [updateState]
  );
  const handleTimezoneChange = useCallback(
    (timezone: string) => updateState({ timezone }),
    [updateState]
  );
  const handleContinue = useCallback(() => {
    navigate(joinStepPath(orgId ?? '', 'permissions'));
  }, [navigate, orgId]);

  // One-shot-per-org hydration of the BLANK fields from the server status.
  //
  // This is an effect, not the compare-in-render shape `flowState.tsx` uses for
  // its `orgId` reload: that one sets the component's OWN state, which React
  // permits, whereas the value here lives in the parent `JoinFlowProvider`, and
  // updating a different component while this one renders is forbidden.
  //
  // It is also not a prop-sync effect (FRONTEND_PATTERNS forbids those): the ref
  // guard keys on `orgId`, so it fires once per org — hydrating from async
  // server data rather than continuously mirroring a prop into state. Keying on
  // `orgId` (not a plain boolean) means switching to another org the invitee was
  // invited to re-hydrates that org's blanks instead of staying suppressed by a
  // ref left set from the first org.
  //
  // The profile query must have SETTLED before we hydrate: it is the canonical
  // source for the timezone, and firing while it is still pending would fall
  // back to the browser zone and clobber a zone the profile is about to supply.
  //
  // Seeds ONLY blank fields, so a value the invitee typed — or deliberately
  // cleared — is never overwritten by a late status settle. The timezone falls
  // back to the BROWSER zone, which is the invitee's own; the org default is the
  // CREATOR's zone, and silently inheriting it is the exact failure this field
  // exists to prevent.
  const hydratedOrgIdRef = useRef<string | null>(null);
  const statusData = status.data;
  const profileUser = profile.data?.user;
  const profileIsPending = profile.isPending;
  useEffect(() => {
    const currentOrgId = orgId ?? '';
    if (
      hydratedOrgIdRef.current === currentOrgId ||
      statusData === undefined ||
      profileIsPending
    ) {
      return;
    }
    hydratedOrgIdRef.current = currentOrgId;
    const patch: Partial<JoinFlowState> = {};
    if (state.firstName === '' && statusData.first_name !== '') {
      patch.firstName = statusData.first_name;
    }
    if (state.lastName === '' && statusData.last_name !== '') {
      patch.lastName = statusData.last_name;
    }
    if (state.timezone === '') {
      // The timezone comes from the PROFILE, the canonical single source — never
      // echoed through the join-onboarding status (see its api.ts comment).
      // `timezone_is_set` is the gate, not `timezone` itself: the serializer always
      // returns a valid zone, resolving an unset one to "UTC", so a truthiness check
      // would silently prefill UTC for someone who never chose a zone. When it was
      // never chosen, the browser's zone is a better guess for THIS user than a
      // stored default, and the field exists for them to correct it either way.
      patch.timezone = profileUser?.timezone_is_set
        ? profileUser.timezone
        : getBrowserTimezone();
    }
    if (Object.keys(patch).length > 0) {
      updateState(patch);
    }
    // `state` is read but deliberately not a dependency: the ref guard makes
    // this a once-per-org hydration, and depending on `state` would re-run it
    // on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, statusData, profileUser, profileIsPending, updateState]);

  const fields = useMemo(
    () => ({
      firstName: { required: true },
      lastName: { required: true },
      role: { required: true },
    }),
    []
  );
  const values = useMemo(
    () => ({
      firstName: state.firstName,
      lastName: state.lastName,
      role: state.role,
    }),
    [state.firstName, state.lastName, state.role]
  );

  if (status.isError) {
    return (
      <div data-testid='join-step-about-you'>
        <p>We couldn&apos;t load your workspace details. Please try again.</p>
      </div>
    );
  }

  if (status.isPending || status.data === undefined) {
    return (
      <div data-testid='join-step-about-you'>
        <LoadingIndicator />
      </div>
    );
  }

  return (
    <FormProvider fields={fields} values={values}>
      <AboutYouStepForm
        firstName={state.firstName}
        onFirstNameChange={handleFirstNameChange}
        lastName={state.lastName}
        onLastNameChange={handleLastNameChange}
        role={state.role}
        onRoleChange={handleRoleChange}
        timezone={state.timezone}
        onTimezoneChange={handleTimezoneChange}
        onContinue={handleContinue}
      />
    </FormProvider>
  );
}
